import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { userAPI } from '../../services/api';
import Modal from '../../components/Modal';
import { LoadingSpinner, EmptyState } from '../../components/UI';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, user: null });
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await userAPI.list({ search, role: roleFilter });
      setUsers(data);
    } catch (e) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchUsers, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, roleFilter]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user permanently?')) return;
    try {
      await userAPI.delete(id);
      toast.success('User deleted');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <h1>Users</h1>
          <p className="subtitle">{users.length} total</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal({ open: true, user: null })}>
          + Add user
        </button>
      </div>

      <div className="flex gap-2 mb-2">
        <input
          className="input"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <select className="select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} style={{ maxWidth: 200 }}>
          <option value="">All roles</option>
          <option value="admin">Admin</option>
          <option value="trainer">Trainer</option>
          <option value="user">User</option>
        </select>
      </div>

      {loading ? <LoadingSpinner /> : users.length === 0 ? (
        <EmptyState icon="👥" title="No users found" />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u._id}>
                  <td><strong>{u.name}</strong></td>
                  <td>{u.email}</td>
                  <td><span className={`badge badge-${u.role}`}>{u.role}</span></td>
                  <td>{u.isActive ? '🟢 Active' : '🔴 Inactive'}</td>
                  <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="flex gap-1">
                      <button className="btn btn-secondary btn-sm" onClick={() => setModal({ open: true, user: u })}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(u._id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <UserModal
        open={modal.open}
        user={modal.user}
        onClose={() => setModal({ open: false, user: null })}
        onSaved={fetchUsers}
      />
    </div>
  );
};

const UserModal = ({ open, user, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name,
        email: user.email,
        role: user.role,
        phone: user.phone || '',
        bio: user.bio || '',
        isActive: user.isActive,
        password: '',
      });
    } else {
      setForm({ name: '', email: '', password: '', role: 'user', phone: '', bio: '', isActive: true });
    }
  }, [user, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (user && !payload.password) delete payload.password;
      if (user) await userAPI.update(user._id, payload);
      else await userAPI.create(payload);
      toast.success(user ? 'User updated' : 'User created');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={user ? 'Edit user' : 'Create user'}>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Name</label>
          <input className="input" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </div>
        <div className="form-group">
          <label>Email</label>
          <input className="input" type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
        </div>
        <div className="form-group">
          <label>Password {user && '(leave blank to keep current)'}</label>
          <input className="input" type="password" value={form.password || ''} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!user} minLength={user ? 0 : 6} />
        </div>
        <div className="grid grid-2">
          <div className="form-group">
            <label>Role</label>
            <select className="select" value={form.role || 'user'} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="user">User</option>
              <option value="trainer">Trainer</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input className="input" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
        </div>
        <div className="form-group">
          <label>Bio</label>
          <textarea className="textarea" value={form.bio || ''} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
        </div>
        {user && (
          <div className="form-group">
            <label>
              <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /> Active
            </label>
          </div>
        )}
        <div className="flex gap-1 justify-between">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
};

export default AdminUsers;
